import React, { useState, useMemo, useRef } from "react";
import { Overlay } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { PERMISSION_CATALOG } from "../../pages/EmployeePermissionPanel";
import { permissionCodesToBoolMap } from "../../utils/permissions";

export default function RolePermissionsOverlay({ role }) {
  const [show, setShow] = useState(false);
  const targetRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setShow(true);
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setShow(false);
    }, 200);
  };

  const tickedPages = useMemo(() => {
    const codes = Array.isArray(role?.permission_codes)
      ? role.permission_codes
      : [];
    if (!codes.length) return [];

    const boolMap = permissionCodesToBoolMap(codes);
    const result = [];

    PERMISSION_CATALOG.forEach((category) => {
      (category.items || []).forEach((item) => {
        const actions = [];
        if (item.view?.length && item.view.some((k) => boolMap[k])) {
          actions.push("View");
        }
        if (item.add?.length && item.add.some((k) => boolMap[k])) {
          actions.push("Add");
        }
        if (item.edit?.length && item.edit.some((k) => boolMap[k])) {
          actions.push("Edit");
        }
        if (item.remove?.length && item.remove.some((k) => boolMap[k])) {
          actions.push("Delete");
        }

        if (actions.length > 0) {
          result.push({
            id: item.id,
            label: item.label,
            actions,
          });
        }
      });
    });

    return result;
  }, [role?.permission_codes]);

  return (
    <>
      <span
        ref={targetRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="d-inline-flex align-items-center justify-content-center"
        style={{
          cursor: "pointer",
          width: "20px",
          height: "20px",
          borderRadius: "50%",
          backgroundColor: show ? "#dbeafe" : "#f1f5f9",
          color: show ? "#2563eb" : "#64748b",
          transition: "all 0.15s ease",
          verticalAlign: "middle",
        }}
        aria-label={`Permissions for ${role?.name}`}
      >
        <Icon icon="solar:info-circle-bold" width="16" height="16" />
      </span>

      <Overlay
        target={targetRef.current}
        show={show}
        placement="right"
        flip
        containerPadding={16}
      >
        {({
          placement: _placement,
          arrowProps: _arrowProps,
          show: _show,
          popper: _popper,
          hasDoneInitialMeasure: _hasDoneInitialMeasure,
          ...props
        }) => (
          <div
            {...props}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{
              ...props.style,
              zIndex: 1070,
            }}
          >
            <div
              className="card shadow-lg border"
              style={{
                width: "280px",
                maxWidth: "90vw",
                borderRadius: "0.75rem",
                overflow: "hidden",
                borderColor: "#e2e8f0",
              }}
            >
              <div
                className="card-body p-2"
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                }}
              >
                {tickedPages.length === 0 ? (
                  <div className="text-muted text-center py-3 small">
                    No permissions ticked for this role.
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {tickedPages.map((page, pIdx) => (
                      <div
                        key={pIdx}
                        className="p-2 rounded border bg-white"
                        style={{ borderColor: "#f1f5f9", fontSize: "0.8rem" }}
                      >
                        <div className="fw-semibold text-dark mb-1">
                          {page.label}
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {page.actions.map((act, aIdx) => (
                            <span
                              key={aIdx}
                              className="badge bg-success-subtle text-success border border-success-subtle"
                              style={{ fontSize: "0.68rem", fontWeight: 500 }}
                            >
                              {act}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Overlay>
    </>
  );
}
